import { Component, OnInit, OnChanges, Input, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { DecimalPipe } from '@angular/common';
import { CommonService } from 'src/app/shared/services/common.service';
import { DeviceService } from '../../device.service';

@Component({
	selector: 'app-battery-performance',
	templateUrl: './battery-performance.component.html',
	styleUrls: ['./battery-performance.component.scss']
})

export class BatteryPerformanceComponent implements OnInit, OnChanges {
	@Input() deviceInfo: any = {};
	@Input() siteAlertsSettings: any = {};

	currentUser: any = {};
	batterySummary: any = {};

	utilizationChartData: any = [];
	remainingLifeAhrsPercentChartData: any = [];
	remainingLifeYrsPercentChartData: any = [];

	utilizationChartOptions: any = {};
	remainingLifeAhrsPercentChartOptions: any = {};
	remainingLifeYrsPercentChartOptions: any = {};

	noDataColor = '#e5e1e1';
	workingDaysOnly: boolean = false;

	pieChartOptions = {
		utilization: {title: this.translate.instant('battery_performance.utilization')},
		remaining_lift_ahrs: {title: this.translate.instant('battery_performance.remaining_life_ahrs')},
		remaining_lift_Yrs: {title: this.translate.instant('battery_performance.remaining_life_Yrs')}
	}

	barHeightsInPixels = 400;
	arrowHeight = 0;
	barMaxPercentage = 150;
	limits = [100, 130, 147];
	limitsColors = ['orange', '#3dd3b2', 'rgb(218, 45, 45)'];

	dailyDetails: any = {};
	invalidDateRange: boolean = false;
	deviceInstallationDate: any = null;
	startDate: Date = moment().subtract(3, 'months').toDate();
	endDate: Date = moment().subtract(1, 'day').toDate();
	maxDate = moment().subtract(1, 'day').toDate();

	batteryBarHeights = {
		total: this.barHeightsInPixels,
		filled: 0,
		red:      ( (this.limits[2] - this.limits[1]) / this.barMaxPercentage) * this.barHeightsInPixels,
		green:    ( (this.limits[1] - this.limits[0]) / this.barMaxPercentage) * this.barHeightsInPixels,
		orange:   ( this.limits[0]                    / this.barMaxPercentage) * this.barHeightsInPixels,
		arrow:    ( this.arrowHeight                  / this.barMaxPercentage) * 100,
		color: this.limitsColors[0]
	};

	constructor(
		private translate: TranslateService,
		private commonService: CommonService,
		private deviceService: DeviceService
	) { }

	ngOnInit() {
		this.utilizationChartOptions = this.getPieChartOptions();
		this.remainingLifeAhrsPercentChartOptions = this.getPieChartOptions();
		this.remainingLifeYrsPercentChartOptions = this.getPieChartOptions();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (this.deviceInfo && this.deviceInfo.mac_address || Object.keys(this.siteAlertsSettings).length) {
			this.deviceInstallationDate = new Date(this.deviceInfo.config_info.installation_date * 1000);
			return this.getDeviceDailyDetails();
		}
	}

	getDeviceDailyDetails() {
		const zoneDiff = new Date().getTimezoneOffset() * -1;
		const from: any = new Date(new Date(this.startDate).getTime() + (zoneDiff * 60 * 1000));
		const to: any = new Date(new Date(this.endDate).getTime() + (zoneDiff * 60 * 1000));

		const fromTime = moment(from).utc().startOf('day').unix();
		const toTime = moment(to).utc().endOf('day').unix();
		if (fromTime > toTime)
			this.invalidDateRange = true;

		if (!this.deviceInfo.mac_address || this.invalidDateRange)
			return;

		this.deviceService.getDailyDetails(this.deviceInfo.mac_address, fromTime, toTime, {ignorePermissionDailyDetailsFields: ['charge_oppurtinity', 'charge_oppurtinity_duration', 'is_working_day']}).subscribe((response: any ) => {
			this.dailyDetails = response;
			return this.prepareData();
		});
	}

	getPieChartOptions() {
		return {
			title: "",
			height: 200,
			width: 250,
			legend: {
				position: 'bottom',
				alignment: 'center',
				textStyle: {
					fontSize: 12,
					color: 'black',
				}
			},
			tooltip: {
				trigger: 'hover',
			},
			slices: {
				0: { color: "#009fe3" },
				1: { color: this.noDataColor },
			},
			pieSliceText: 'percentage',
			pieSliceTextStyle: {
				fontSize: 14
			},
			chartArea: { width: '100%' },
		};
	}

	prepareData() {
		const workingDaysOnly = (this.batterySummary && this.batterySummary.workingDaysOnly) || false;
		const calculatedValues= this.generateDailyDetailsSummaryData();
		this.batterySummary = this.calculateDailyDetails(workingDaysOnly);

		this.batterySummary.hideEBU = calculatedValues.totalchargeAHR < this.deviceInfo.config_info.battery_capacity || calculatedValues.totalDischargeAHR < this.deviceInfo.config_info.battery_capacity;
		this.batterySummary.hideEBU = this.batterySummary.hideEBU || (calculatedValues.totalDischargeAHR / (0.8 * this.deviceInfo.config_info.battery_capacity * calculatedValues.countOfWorkingDays) < 0.25);

		this.batterySummary.ahrs_return_text =  Math.round(this.batterySummary.ahrReturn);
		this.batterySummary.ahrs_return_width = this.batterySummary.ahrReturn;

		if (this.batterySummary.ahrs_return_width > 165)
			this.batterySummary.ahrs_return_width = 165;

		this.batterySummary.ahrs_return_width /=1.65;

		if (this.batterySummary.minChargeOppurtinityDuration > 8*60*60 )
			this.batterySummary.minChargeOppurtinityDuration = 8*60*60;


		this.batterySummary.min_charge_oppurtinity_width = ( this.batterySummary.minChargeOppurtinityDuration / (8*60*60) ) * 100;
		this.batterySummary.min_charge_oppurtinity_duration = this.commonService.timeFormat(this.batterySummary.minChargeOppurtinityDuration);

		/* remaining life Ah */
		this.batterySummary.remaining_life_ahrs_percent = this.calcProportion((this.batterySummary.totalAhrs - this.batterySummary.usedAhrs), this.batterySummary.usedAhrs);
		this.batterySummary.remaining_life_months_per_ahrs = this.batterySummary.remaingMonthsPerUsage;


		/* remaining life Year */
		this.batterySummary.remaining_life_yrs = [this.batterySummary.usedLifeYears, this.batterySummary.totalLifeYears - this.batterySummary.usedLifeYears]
		this.batterySummary.remaining_life_yrs_percent = this.calcProportion(this.batterySummary.remaining_life_yrs[1], this.batterySummary.remaining_life_yrs[0]);
		this.applyBatterySummaryToCharts();
	}

	calculateDailyDetails(workingDaysOnly=false) {
		let decimalPipe: DecimalPipe = new DecimalPipe("en-US");
		let countOfWorkingDays = 0,
			totalInuseAs = 0,
			maxDailyInuse = 0,
			totalChargeAs = 0;

		const calculatedDailyDetails = {
			batteryUtilization: 0,
			avgDailyEBU: '0',
			ahrReturn: 0,
			maxDailyEBU: '0',
			workingDaysOnly: workingDaysOnly,
			usedAhrs: 0,
			totalAhrs: 0,
			remaingMonthsPerUsage: '',
			totalLifeYears:0,
			usedLifeYears: 0,
			minChargeOppurtinityDuration: 99999,
		};


		let expectedEBU;
		switch (this.deviceInfo.config_info.charger_type) {
			case this.deviceService.chargerTypes.Conventional:
				expectedEBU = this.siteAlertsSettings.conventional_ebu_limit || 1;
				break;
			case this.deviceService.chargerTypes.Fast:
				expectedEBU = this.siteAlertsSettings.fast_ebu_limit || 1.6;
				break;
			case this.deviceService.chargerTypes.Opportunity:
				expectedEBU = this.siteAlertsSettings.opportunity_ebu_limit || 1.25;
				break;
			default:
				expectedEBU = 1;
				break;
		}

		let sortedDailyDetails = [];
		if (this.dailyDetails.length > 0)
			sortedDailyDetails = this.dailyDetails.sort((a,b) => {return (a.date < b.date) ? 1 : ((b.date < a.date) ? -1 : 0);});

		for (let dayIndex in sortedDailyDetails) {
			let currentDayDailyDetails = sortedDailyDetails[dayIndex];
			let inuseAsValue = currentDayDailyDetails.inuse_events_as;
			let chargeAsValue = currentDayDailyDetails.charge_events_as;

			totalChargeAs += chargeAsValue;

			if (currentDayDailyDetails.is_working_day) {
				totalInuseAs += inuseAsValue;
				const dayOfWeekNum = moment(currentDayDailyDetails.date*1000).utc().day();
				if (!calculatedDailyDetails.workingDaysOnly || this.isWorkingDay(dayOfWeekNum))
					countOfWorkingDays++;

				if (currentDayDailyDetails.charge_oppurtinity > 0) {
					if (currentDayDailyDetails.charge_oppurtinity_duration < calculatedDailyDetails.minChargeOppurtinityDuration) {
						calculatedDailyDetails.minChargeOppurtinityDuration = currentDayDailyDetails.charge_oppurtinity_duration;
					}
				}
			}

			if (inuseAsValue > maxDailyInuse)
				maxDailyInuse = inuseAsValue;
		}

		if (countOfWorkingDays > 0) {
			if (totalInuseAs > 0) {
				const averageInuseAhr = (totalInuseAs / countOfWorkingDays) / 3600;
				let averageInuseAhrOverEBU = (this.deviceInfo.config_info.battery_capacity) ? averageInuseAhr / (0.8 * this.deviceInfo.config_info.battery_capacity) : 0;
				calculatedDailyDetails.batteryUtilization = +((averageInuseAhrOverEBU / expectedEBU) * 100)?.toFixed(2);

				if (calculatedDailyDetails.batteryUtilization > 100)
					calculatedDailyDetails.batteryUtilization = 100;

				if (averageInuseAhrOverEBU > 3)
					averageInuseAhrOverEBU = 3;

				calculatedDailyDetails.avgDailyEBU = decimalPipe.transform((averageInuseAhrOverEBU).toFixed(2));
			}
		}

		if (totalInuseAs != 0)
			calculatedDailyDetails.ahrReturn = (totalChargeAs / totalInuseAs) * 100;

		let max_daily_ebu = (this.deviceInfo.config_info.battery_capacity) ? maxDailyInuse / (3600 * 0.8 * this.deviceInfo.config_info.battery_capacity) : 0;
		if (max_daily_ebu > 3)
			max_daily_ebu = 3;

		calculatedDailyDetails.maxDailyEBU = decimalPipe.transform((max_daily_ebu).toFixed(2));

		if (calculatedDailyDetails.minChargeOppurtinityDuration == 99999)
			calculatedDailyDetails.minChargeOppurtinityDuration = 0;

		let totalAhrs	=  1250 * (+this.deviceInfo.config_info.battery_capacity),
			usedAhrs	= +this.deviceInfo.config_info.inuse_ahr + (+this.deviceInfo.config_info.add_inuse_ahr);

		if(usedAhrs > totalAhrs)
			usedAhrs = totalAhrs;

		let installationDate		= new Date(this.deviceInfo.config_info.installation_date*1000),
			currentDay				= new Date(),
			deviceManufacturingDate	= new Date(this.deviceInfo.config_info.truck_manufacturing_year, 0, 1),
			remaingMonthsPerUsage	= 0,
			batteryUsagePerMonth	= 0,
			maxRemainingMonths		= 0,
			totalLifeYears			= 5,
			usedLifeYears,
			monthsSinceInstallation;

		if (deviceManufacturingDate.getTime() > 1*24*60*60*1000) {
			/* get month in it */
			monthsSinceInstallation = this.getElapsedMonths(deviceManufacturingDate, currentDay);
		} else {
			monthsSinceInstallation = this.getElapsedMonths(installationDate, currentDay);
		}

		if (deviceManufacturingDate.getTime() > 1*24*60*60*1000) {
			usedLifeYears = parseFloat((this.getElapsedMonths(deviceManufacturingDate, currentDay) / 12).toFixed(1));
			if (deviceManufacturingDate < installationDate) {
				const duration = moment.duration(installationDate.getTime() - deviceManufacturingDate.getTime());
				usedAhrs	+= duration.asYears() * batteryUsagePerMonth * 12;
				if (usedAhrs > totalAhrs) {
					usedAhrs = totalAhrs;
				}
			}
		} else {
			usedLifeYears = parseFloat((this.getElapsedMonths(installationDate, currentDay) / 12).toFixed(1));
		}

		if (this.deviceInfo.config_info.charger_type == this.deviceService.chargerTypes.Fast)
			totalLifeYears = 4;

		maxRemainingMonths = totalLifeYears * 12;

		if (monthsSinceInstallation > 0) {
			batteryUsagePerMonth = usedAhrs / monthsSinceInstallation;
			remaingMonthsPerUsage = batteryUsagePerMonth ? (totalAhrs - usedAhrs) / batteryUsagePerMonth : 0;
		}

		if (remaingMonthsPerUsage < 0)
			remaingMonthsPerUsage = 0;

		if(remaingMonthsPerUsage > maxRemainingMonths)
			remaingMonthsPerUsage = maxRemainingMonths;

		if (usedLifeYears > totalLifeYears)
			usedLifeYears = totalLifeYears;

		/* remaining life Ah */
		calculatedDailyDetails.usedAhrs = usedAhrs;
		calculatedDailyDetails.totalAhrs = totalAhrs;
		calculatedDailyDetails.remaingMonthsPerUsage = decimalPipe.transform(remaingMonthsPerUsage.toFixed(0));

		/* remaining  life Year */
		calculatedDailyDetails.usedLifeYears = usedLifeYears;
		calculatedDailyDetails.totalLifeYears = totalLifeYears;

		return calculatedDailyDetails;
	}

	isWorkingDay(dayOfWeekNum) {
		return dayOfWeekNum > 0 && dayOfWeekNum < 6;
	}

	calcProportion = (a, b) => {
		return Math.round(a / (a + b) * 100);
	}

	getElapsedMonths(start, end) {
		let months;
		months = (end.getFullYear() - start.getFullYear()) * 12;
		months -= start.getMonth() + 1;
		months += end.getMonth();
		return months <= 0 ? 0 : months;
	}

	applyBatterySummaryToCharts() {
		const batteryUtilization = +(this.batterySummary.batteryUtilization || 0).toFixed(1);
		const remainingUtilization = +(100 - this.batterySummary.batteryUtilization || 0).toFixed(1);
		this.utilizationChartData = [
			[`${batteryUtilization}%`, batteryUtilization, `${batteryUtilization}%`],
			[`${remainingUtilization}%`, remainingUtilization, `${remainingUtilization}%`],
		];

		const remainingLifeAhrsPercent = +(this.batterySummary.remaining_life_ahrs_percent || 0).toFixed(1);
		const remainingLifeAhrsPercentRemaining = +(100 - this.batterySummary.remaining_life_ahrs_percent || 0).toFixed(1);
		this.remainingLifeAhrsPercentChartData = [
			[`${remainingLifeAhrsPercent}%`, remainingLifeAhrsPercent, `${remainingLifeAhrsPercent}%`],
			[`${remainingLifeAhrsPercentRemaining}%`, remainingLifeAhrsPercentRemaining, `${remainingLifeAhrsPercentRemaining}%`],
		];

		const remainingLifeYrsPercent = +(this.batterySummary.remaining_life_yrs_percent || 0).toFixed(1);
		const remainingLifeYrsPercentRemaining = +(100 - this.batterySummary.remaining_life_yrs_percent || 0).toFixed(1);
		this.remainingLifeYrsPercentChartData = [
			[`${remainingLifeYrsPercent}%`, remainingLifeYrsPercent, `${remainingLifeYrsPercent}%`],
			[`${remainingLifeYrsPercentRemaining}%`, remainingLifeYrsPercentRemaining, `${remainingLifeYrsPercentRemaining}%`],
		];

		this.arrowHeight = this.batterySummary.ahrs_return_text;
		if (this.arrowHeight) {
			this.batteryBarHeights = {
				filled:   ( this.arrowHeight                  / this.barMaxPercentage) * this.barHeightsInPixels,
				total:     this.barHeightsInPixels,
				red:      ( (this.limits[2] - this.limits[1]) / this.barMaxPercentage) * this.barHeightsInPixels,
				green:    ( (this.limits[1] - this.limits[0]) / this.barMaxPercentage) * this.barHeightsInPixels,
				orange:   ( this.limits[0]                    / this.barMaxPercentage) * this.barHeightsInPixels,
				arrow:    ( this.arrowHeight                  / this.barMaxPercentage) * 100,
				color: this.arrowHeight < this.limits[0] ? this.limitsColors[0] : this.arrowHeight < this.limits[1] ? this.limitsColors[1] : this.limitsColors[2]
			};
		}

		if (this.batteryBarHeights.filled > this.barHeightsInPixels)
			this.batteryBarHeights.filled = this.barHeightsInPixels;
	}

	generateDailyDetailsSummaryData() {
		let DailyDetails: any = {};
		DailyDetails.totalDischargeAHR = 0;
		DailyDetails.totalchargeAHR = 0;
		DailyDetails.countOfWorkingDays = 0;
		DailyDetails.totalWorkingDays = 0;

		let sortedDailyDetails = [];
		if (this.dailyDetails.length > 0)
			sortedDailyDetails = this.dailyDetails.sort((a,b) => {return (a.date < b.date) ? 1 : ((b.date < a.date) ? -1 : 0);});

		for (let index in sortedDailyDetails) {
			const currentDay = this.dailyDetails[index];

			let inuseAsValue = currentDay.inuse_events_as;
			let chargeAsValue = currentDay.charge_events_as;


			let inuse_ah = (+inuseAsValue) / 3600;
			let charge_ah = +(chargeAsValue) / 3600;

			let dayOfWeekNum = new Date(currentDay.date * 1000).getDay();

			if (currentDay.is_working_day) {
				DailyDetails.totalWorkingDays++;
				if ([6,0].indexOf(dayOfWeekNum) == -1)
					DailyDetails.countOfWorkingDays++;
			}

			if (inuse_ah != 0)
				DailyDetails.totalDischargeAHR += inuse_ah;

			DailyDetails.totalchargeAHR += charge_ah;
		}

		return DailyDetails;
	}
}
